import React, { useState, useEffect } from "react";
import { Form, Input, Button, notification } from "antd";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  TeamOutlined,
  SecurityScanOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [currentWelcomeText, setCurrentWelcomeText] = useState(0);
  const [fade, setFade] = useState(true);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const welcomeTexts = [
    "Welcome to Serena BEDS LTD Staff Portal",
    "Your Gateway to Efficient Operations",
    "Streamlined Staff Management System",
    "Secure Access to Work Tools",
    "Empowering Your Daily Workflow",
    "Professional Staff Dashboard",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentWelcomeText((prev) => (prev + 1) % welcomeTexts.length);
        setFade(true);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const onFinish = async (values) => {
    setLoading(true);

    localStorage.removeItem("token");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");

    try {
      const { email, password } = values;
      const response = await axiosInstance.post("/auth/user/login", {
        email,
        password,
      });

      if (response.data.success) {
        const user = response.data.user;

        const allowedRoles = [
          "admin",
          "super admin",
          "sales",
          "deliveries",
          "production manager",
          "production admin",
        ];

        if (user && user.role && allowedRoles.includes(user.role.toLowerCase())) {
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("userRole", user.role.toLowerCase());

          if (user.id) localStorage.setItem("userId", user.id);
          if (user.full_name) localStorage.setItem("userName", user.full_name);

          notification.success({
            message: response.data.message || "Login successful!",
            description: "Welcome back to your dashboard!",
            placement: "topRight",
            duration: 2.5,
          });

          const role = user.role.toLowerCase();
          let target = "/";
          if (role === "deliveries") target = "/order-signed-delivery-note";
          else if (role === "sales") target = "/all-orders";
          else if (role === "production admin" || role === "production manager") {
            target = "/production-dashboard";
          }

          setTimeout(() => navigate(target), 2500);
        } else {
          form.setFields([
            {
              name: "email",
              errors: ["You are not authorized to access this portal."],
            },
          ]);

          notification.error({
            message: "Access Denied",
            description: "You are not authorized to access this portal.",
            placement: "topRight",
          });
        }
      } else {
        const errorMessage = response.data.message || "Invalid email or password.";

        form.setFields([
          { name: "email", errors: [errorMessage] },
          { name: "password", errors: [errorMessage] },
        ]);

        notification.error({
          message: "Login Failed",
          description: errorMessage,
          placement: "topRight",
        });
      }
    } catch (error) {
      // ✅ ✅ ✅ THIS IS THE ONLY REQUIRED FIX
      if (window.__IS_LOGGING_OUT__) return;

      let errMsg = "An error occurred. Please try again.";

      if (error.response) {
        if (error.response.status === 401) {
          errMsg = "Invalid email or password.";
        } else if (error.response.data && error.response.data.message) {
          errMsg = error.response.data.message;
        } else {
          errMsg = `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errMsg = "Network error. Please check your connection.";
      } else {
        errMsg = error.message || "An unexpected error occurred.";
      }

      form.setFields([
        { name: "email", errors: [errMsg] },
        { name: "password", errors: [errMsg] },
      ]);

      notification.error({
        message: "Login Failed",
        description: errMsg,
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearErrors = () => {
    form.setFields([
      { name: "email", errors: [] },
      { name: "password", errors: [] },
    ]);
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat relative overflow-hidden"
      style={{
        backgroundImage: "url('/login.png')",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-indigo-900/80 z-1" />

      <div className="absolute inset-0 overflow-hidden z-2">
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-xl animate-float-slow"></div>
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-purple-400/15 rounded-full blur-2xl animate-float-medium"></div>
        <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-blue-300/10 rounded-full blur-lg animate-float-fast"></div>
        <div className="absolute bottom-1/4 right-1/3 w-32 h-32 bg-indigo-300/10 rounded-full blur-lg animate-float-slow"></div>

        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>
        </div>
      </div>

      <div className="absolute inset-0 z-3">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex justify-center items-center min-h-screen p-6">
        <div className="w-full max-w-md bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 transform transition-all duration-500 hover:shadow-3xl border border-white/30 relative overflow-hidden">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-3xl blur opacity-30"></div>

          <div className="text-center mb-8 relative z-10">
            <div className="w-28 h-28 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl relative overflow-hidden group animate-float-slow">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
              <TeamOutlined className="text-white text-4xl transform group-hover:scale-110 transition-transform duration-300" />
            </div>

            <div className="h-20 mb-4 flex items-center justify-center">
              <div className="relative">
                <h1
                  className={`text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-700 bg-clip-text text-transparent transition-all duration-500 transform ${
                    fade
                      ? "opacity-100 translate-y-0 scale-100"
                      : "opacity-0 -translate-y-4 scale-95"
                  }`}
                >
                  {welcomeTexts[currentWelcomeText]}
                </h1>
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent transform scale-x-0 transition-transform duration-500 group-hover:scale-x-100"></div>
              </div>
            </div>

            <p className="text-gray-600 font-medium text-lg flex items-center justify-center gap-2">
              <SecurityScanOutlined className="text-green-500" />
              Staff Authentication Portal
            </p>
          </div>

          <Form
            form={form}
            name="login_form"
            initialValues={{ rememberMe: true }}
            onFinish={onFinish}
            layout="vertical"
            className="space-y-6 relative z-10"
          >
            <Form.Item
              name="email"
              label={
                <span className="text-gray-700 font-semibold text-base flex items-center gap-2">
                  <UserOutlined /> Work Email
                </span>
              }
              rules={[
                { required: true, message: "Please enter your work email" },
                { type: "email", message: "Please enter a valid email address" },
              ]}
              className="mb-0"
              validateStatus={form.getFieldError("email").length > 0 ? "error" : ""}
              help={form.getFieldError("email")[0]}
            >
              <Input
                prefix={<UserOutlined className="text-gray-400 text-lg" />}
                placeholder="Enter your company email"
                size="large"
                className="rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 py-4 px-4 bg-white/70 text-lg h-14"
                autoComplete="email"
                onChange={() => clearErrors("email")}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span className="text-gray-700 font-semibold text-base flex items-center gap-2">
                  <LockOutlined /> Password
                </span>
              }
              rules={[
                { required: true, message: "Please enter your password" },
                { min: 6, message: "Password must be at least 6 characters" },
              ]}
              className="mb-0"
              validateStatus={form.getFieldError("password").length > 0 ? "error" : ""}
              help={form.getFieldError("password")[0]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400 text-lg" />}
                placeholder="Enter your secure password"
                size="large"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone className="text-lg" /> : <EyeInvisibleOutlined className="text-lg" />
                }
                className="rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 py-4 px-4 bg-white/70 text-lg h-14"
                autoComplete="current-password"
                onChange={() => clearErrors("password")}
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="h-14 bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 border-0 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center justify-center text-lg">
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Verifying Credentials...
                    </>
                  ) : (
                    <>
                      <DashboardOutlined className="mr-3 text-xl" />
                      Access Staff Dashboard
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl animate-pulse"></div>
              </Button>
            </Form.Item>
          </Form>

          <div className="mt-6 text-center relative z-10">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <p className="text-green-600 font-medium text-sm flex items-center gap-1">
                <SecurityScanOutlined className="text-xs" />
                Secure & Encrypted
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 text-center">
          <p className="text-white/90 text-base font-semibold backdrop-blur-xl bg-black/30 py-3 px-6 rounded-full inline-block border border-white/20 shadow-2xl">
            © 2024 <span className="font-bold text-white">Serena</span> • BEDS LTD
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(1deg);
          }
        }

        @keyframes float-medium {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(-1deg);
          }
        }

        @keyframes float-fast {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-float-medium {
          animation: float-medium 4s ease-in-out infinite;
        }

        .animate-float-fast {
          animation: float-fast 3s ease-in-out infinite;
        }

        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }

        .fade-in {
          animation: fadeIn 0.8s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
